import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getReconciliation,
  refreshReconciliation,
  deletePersistentGhost,
  type AdminReconcileFinding,
  type AdminReconcileProvider,
  type AdminReconcileResponse,
  type AdminReconcileSeverity,
  type AdminPersistentGhost,
} from "@admin/lib/api";
import { formatDate } from "@admin/lib/utils";
import {
  formatFindingValue,
  formatProviderTotals,
  RECONCILE_VALUE_LEGEND,
} from "@admin/lib/reconcile-display";
import { Button } from "@admin/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";
import { cn } from "@admin/lib/utils";

const PROVIDERS: AdminReconcileProvider[] = ["openrouter", "composio", "hetzner", "telemetry"];
const SEVERITIES: Array<AdminReconcileSeverity | "all"> = ["all", "critical", "warn", "info"];

function statusClass(status: string): string {
  if (status === "ok") return "bg-emerald-500/20 text-emerald-300";
  if (status === "drift") return "bg-amber-500/20 text-amber-200";
  return "bg-danger/20 text-danger";
}

function severityClass(severity: string): string {
  if (severity === "critical") return "bg-danger/20 text-danger";
  if (severity === "warn") return "bg-amber-500/20 text-amber-200";
  return "bg-muted text-muted-fg";
}

function providerLabel(p: string): string {
  if (p === "openrouter") return "AI models";
  if (p === "composio") return "Connected apps";
  if (p === "hetzner") return "Workspace hosting";
  if (p === "telemetry") return "Telemetry (workspace)";
  return p;
}

function SummaryCard({
  provider,
  run,
}: {
  provider: AdminReconcileProvider;
  run: AdminReconcileResponse["runs"][number] | undefined;
}) {
  const bySeverity = (run?.summary.findingsBySeverity ?? {}) as Record<string, number>;
  const warnCount = (bySeverity.warn ?? 0) + (bySeverity.critical ?? 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-fg">{providerLabel(provider)}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {run ? (
          <>
            <span
              className={cn(
                "inline-block rounded-full px-2 py-0.5 text-xs font-medium",
                statusClass(run.status),
              )}
            >
              {run.status}
            </span>
            <div className="text-xs text-muted-fg">Last run: {formatDate(run.finishedAt)}</div>
            {typeof run.summary.usersChecked === "number" && (
              <div className="text-xs text-muted-fg">
                Users checked: {run.summary.usersChecked}
              </div>
            )}
            {run.summary.internalTotal != null && run.summary.externalTotal != null && (
              <div className="text-sm tabular-nums">
                {formatProviderTotals(provider, run.summary.internalTotal, run.summary.externalTotal)}
              </div>
            )}
            <div className="text-sm">
              {warnCount > 0 ? (
                <span className="text-amber-200">{warnCount} drift finding(s)</span>
              ) : (
                <span className="text-muted-fg">No drift</span>
              )}
            </div>
          </>
        ) : (
          <div className="text-sm text-muted-fg">No runs yet</div>
        )}
      </CardContent>
    </Card>
  );
}

function FindingRow({ finding }: { finding: AdminReconcileFinding }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr
        className="cursor-pointer border-t border-border hover:bg-muted/30"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="px-3 py-2">
          <span
            className={cn(
              "inline-block rounded-full px-2 py-0.5 text-xs",
              severityClass(finding.severity),
            )}
          >
            {finding.severity}
          </span>
        </td>
        <td className="px-3 py-2 text-xs text-muted-fg">{providerLabel(finding.provider)}</td>
        <td className="px-3 py-2 font-mono text-xs">{finding.kind}</td>
        <td className="px-3 py-2">{finding.title}</td>
        <td className="px-3 py-2 tabular-nums text-xs">{formatFindingValue(finding, "internal")}</td>
        <td className="px-3 py-2 tabular-nums text-xs">{formatFindingValue(finding, "external")}</td>
        <td className="px-3 py-2 tabular-nums text-xs">{formatFindingValue(finding, "diff")}</td>
        <td className="px-3 py-2 text-xs">
          {finding.userId ? (
            <Link
              to={`/users/${finding.userId}`}
              className="text-accent hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {finding.userId.slice(0, 8)}...
            </Link>
          ) : (
            "-"
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-t border-border bg-muted/20">
          <td colSpan={8} className="px-3 py-3">
            <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-muted-fg">
              {JSON.stringify(finding.detail, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function PersistentGhostsCard({
  ghosts,
  onDeleted,
}: {
  ghosts: AdminPersistentGhost[];
  onDeleted: () => void;
}) {
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  if (ghosts.length === 0) return null;

  const onDelete = async (ghost: AdminPersistentGhost) => {
    if (!ghost.user_id || !ghost.safe_to_delete) return;
    setDeletingId(ghost.hetzner_server_id);
    setDeleteErr(null);
    try {
      await deletePersistentGhost(ghost.hetzner_server_id, ghost.user_id);
      onDeleted();
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card className="border-amber-500/40 bg-amber-500/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-amber-200">Persistent ghost servers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-fg">
          Labelled Hetzner servers with no matching active workspace row. Persistent ghosts
          appeared in multiple daily reconciles for 24h+.
        </p>
        {deleteErr && <p className="text-sm text-danger">{deleteErr}</p>}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/30 text-xs text-muted-fg">
              <tr>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Server id</th>
                <th className="px-3 py-2">First seen</th>
                <th className="px-3 py-2">Last seen</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {ghosts.map((ghost) => (
                <tr key={ghost.hetzner_server_id} className="border-t border-border">
                  <td className="px-3 py-2 text-xs">
                    {ghost.user_id ? (
                      <Link to={`/users/${ghost.user_id}`} className="text-accent hover:underline">
                        {ghost.email ?? `${ghost.user_id.slice(0, 8)}...`}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{ghost.hetzner_server_id}</td>
                  <td className="px-3 py-2 text-xs">{formatDate(ghost.first_seen_at)}</td>
                  <td className="px-3 py-2 text-xs">{formatDate(ghost.last_seen_at)}</td>
                  <td className="px-3 py-2">
                    {ghost.persistent ? (
                      <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-200">
                        persistent
                      </span>
                    ) : (
                      <span className="text-xs text-muted-fg">recent</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!ghost.safe_to_delete || deletingId === ghost.hetzner_server_id}
                      title={
                        ghost.safe_to_delete
                          ? "Delete labelled ghost server in Hetzner"
                          : "Blocked: user still has active workspace or pending rebuild"
                      }
                      onClick={() => void onDelete(ghost)}
                    >
                      {deletingId === ghost.hetzner_server_id ? "Deleting..." : "Delete ghost"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OpsPage() {
  const [data, setData] = useState<AdminReconcileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [providerFilter, setProviderFilter] = useState<AdminReconcileProvider | "all">("all");
  const [severityFilter, setSeverityFilter] = useState<AdminReconcileSeverity | "all">("all");

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setData(
        await getReconciliation({
          provider: providerFilter === "all" ? undefined : providerFilter,
          severity: severityFilter === "all" ? undefined : severityFilter,
        }),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load reconciliation");
    } finally {
      setLoading(false);
    }
  }, [providerFilter, severityFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    setErr(null);
    try {
      await refreshReconciliation();
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const runsByProvider = useMemo(() => {
    const map = new Map<string, AdminReconcileResponse["runs"][number]>();
    for (const r of data?.runs ?? []) map.set(r.provider, r);
    return map;
  }, [data]);

  const findings = data?.findings ?? [];
  const persistentGhosts = data?.persistent_ghosts ?? [];
  const driftCount = findings.filter((f) => f.severity === "warn" || f.severity === "critical").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Reconciliation</h1>
        <Button size="sm" onClick={() => void onRefresh()} disabled={refreshing || loading}>
          {refreshing ? "Refreshing..." : "Refresh now"}
        </Button>
      </div>

      <p className="text-sm text-muted-fg">
        Compare internal spend calculations against external provider reports. Click a row to expand
        drill-down details.
      </p>
      <p className="text-xs text-muted-fg">{RECONCILE_VALUE_LEGEND}</p>

      {err && <p className="text-danger">{err}</p>}

      <PersistentGhostsCard ghosts={persistentGhosts} onDeleted={() => void load()} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PROVIDERS.map((p) => (
          <SummaryCard key={p} provider={p} run={runsByProvider.get(p)} />
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="self-center text-xs text-muted-fg">Provider:</span>
        {(["all", ...PROVIDERS] as const).map((f) => (
          <Button
            key={f}
            variant={providerFilter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setProviderFilter(f)}
          >
            {f === "all" ? "All" : providerLabel(f)}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="self-center text-xs text-muted-fg">Severity:</span>
        {SEVERITIES.map((f) => (
          <Button
            key={f}
            variant={severityFilter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setSeverityFilter(f)}
          >
            {f === "all" ? "All" : f}
            {f === "all" && driftCount > 0 ? ` (${driftCount} drift)` : ""}
          </Button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/30 text-xs text-muted-fg">
            <tr>
              <th className="px-3 py-2">Severity</th>
              <th className="px-3 py-2">Provider</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Internal (ours)</th>
              <th className="px-3 py-2">External (provider)</th>
              <th className="px-3 py-2">Diff</th>
              <th className="px-3 py-2">User</th>
            </tr>
          </thead>
          <tbody>
            {loading && findings.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-muted-fg">
                  Loading...
                </td>
              </tr>
            ) : findings.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-muted-fg">
                  No findings. Run reconciliation or adjust filters.
                </td>
              </tr>
            ) : (
              findings.map((f) => <FindingRow key={f.id} finding={f} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
